import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { supabase } from "../lib/supabaseClient";
import { JUNGNA_STORAGE, JUNGNA_TABLES } from "../lib/jungnaTables";
import { ProductContext } from "../context/ProductContext";
import AdminLogin from "../components/Admin/AdminLogin";
import type { Product, Bank, Seller, ProductInfo as AdminProductInfo } from "../types/admin";
import MultiImageUploader, { ProductImageItem } from "../components/Admin/MultiImageUploader";
import AdminLoginData, { LoginEntry } from "../components/Admin/AdminLoginData";
import AdminOrders, { OrderEntry } from "../components/Admin/AdminOrders";

interface AdminPageProps {}

enum TabType {
  URL = "URL",
  PRODUCT = "PRODUCT",
  SELLER = "SELLER",
  BANK = "ACCOUNT",
  LOGIN = "LOGIN",
  ORDERS = "ORDERS",
}

enum AddProductStep {
  PRODUCT_INFO = 0,
  SELLER_SELECT = 1,
  BANK_SELECT = 2,
  URL_MAPPING = 3,
}

interface URLMapping {
  id: string;
  url: string;
  product_id: string;
  seller_id: string;
  bank_id: string;
  is_active: boolean;
  created_at: string;
}

interface AddProductModalState {
  isOpen: boolean;
  currentStep: AddProductStep;
  productInfo: AdminProductInfo;
  selectedSellerId: string | null;
  selectedBankId: string | null;
  urlMapping: string;
}

// Container 컴포넌트를 파일 상단으로 이동
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const AdminPage: React.FC<AdminPageProps> = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>(TabType.URL);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [loginEntries, setLoginEntries] = useState<LoginEntry[]>([]);
  const [visibleLoginEntries, setVisibleLoginEntries] = useState<number>(20);
  const [isLoadingLoginData, setIsLoadingLoginData] = useState<boolean>(false);
  const [bankInfo, setBankInfo] = useState({
    bankName: "",
    accountNumber: "",
  });
  const [banks, setBanks] = useState<Bank[]>([]);
  const [newBank, setNewBank] = useState({
    name: "",
    account_number: "",
    is_active: true,
  });
  const [products, setProducts] = useState<Product[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [selectedSeller, setSelectedSeller] = useState<string>("");
  const [activeBankId, setActiveBankId] = useState<string | null>(null);
  const [visibleProducts, setVisibleProducts] = useState<number>(6);
  const [visibleSellers, setVisibleSellers] = useState<number>(6);
  const [visibleBanks, setVisibleBanks] = useState<number>(6);
  const [urlMappings, setUrlMappings] = useState<URLMapping[]>([]);
  const [productImageList, setProductImageList] = useState<ProductImageItem[]>([]);
  // 새 상품 추가 모달에서 미리 선택한 이미지 파일들 (상품 생성 후 업로드)
  const [newProductFiles, setNewProductFiles] = useState<File[]>([]);
  const [newMapping, setNewMapping] = useState<Partial<URLMapping>>({
    url: "",
    product_id: "",
    seller_id: "",
    bank_id: "",
    is_active: true,
  });
  const [showSellerForm, setShowSellerForm] = useState(false);
  const [addProductModal, setAddProductModal] = useState<AddProductModalState>({
    isOpen: false,
    currentStep: AddProductStep.PRODUCT_INFO,
    productInfo: {
      title: "",
      description: "",
      price: "",
      topCategory: "",
      bottomCategory: "",
      status: "판매중",
      sellerName: "",
      sellerContact: "",
      content: "",
      image_url: "",
      date: new Date().toISOString(),
      views: 0,
    },
    selectedSellerId: null,
    selectedBankId: null,
    urlMapping: "",
  });

  const context = useContext(ProductContext);
  if (!context) {
    throw new Error("ProductContext를 찾을 수 없습니다.");
  }

  const {
    productInfo,
    setProductInfo,
    sellerInfo,
    productImage,
    setProductImage,
  } = context;

  // 로그아웃 처리 함수
  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await supabase.auth.signOut();
      setIsAuthenticated(false);
      setUserId(null);
      setMessage("로그아웃되었습니다.");
    } catch (error) {
      console.error("로그아웃 에러:", error);
      setError("로그아웃 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 인증 상태 체크
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from(JUNGNA_TABLES.profiles)
            .select("*")
            .eq("id", user.id)
            .single();

          if (profile) {
            const subscriptionEnd = new Date(profile.subscription_end);
            const now = new Date();
            if (subscriptionEnd > now && profile.is_active) {
              setIsAuthenticated(true);
              setUserId(user.id); // 사용자 ID 저장

              // 여기서 은행 정보 로드
              const { data: products } = await supabase
                .from(JUNGNA_TABLES.products)
                .select("*")
                .eq("id", selectedProduct)
                .eq("user_id", user.id)
                .single();

              if (products) {
                setBankInfo({
                  bankName: products.bank_name || "",
                  accountNumber: products.bank_account || "",
                });
              }
              return;
            }
          }
        }
        setIsAuthenticated(false);
      } catch (error) {
        console.error("Auth check error:", error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [selectedProduct]);

  // 은행 목록 불러오기
  useEffect(() => {
    const fetchBanks = async () => {
      if (!userId) return;

      const { data, error } = await supabase
        .from(JUNGNA_TABLES.banks)
        .select("*")
        .eq("user_id", userId)
        .order("name");

      if (error) {
        console.error("은행 목록을 가져오는데 실패했습니다:", error);
        return;
      }

      setBanks(data || []);
    };

    if (isAuthenticated && userId) {
      fetchBanks();
    }
  }, [isAuthenticated, userId]);

  // 새 은행 추가
  const handleAddBank = async () => {
    try {
      if (!userId) {
        alert("로그인이 필요합니다.");
        return;
      }

      const { error } = await supabase
        .from(JUNGNA_TABLES.banks)
        .insert([{ ...newBank, user_id: userId }])
        .select();

      if (error) throw error;

      // 새로운 은행 목록을 가져옴
      const { data: updatedBanks, error: fetchError } = await supabase
        .from(JUNGNA_TABLES.banks)
        .select("*")
        .eq("user_id", userId)
        .order("name");

      if (fetchError) throw fetchError;

      setBanks(updatedBanks || []);
      setNewBank({ name: "", account_number: "", is_active: true });
      alert("은행이 추가되었습니다.");
    } catch (error) {
      console.error("은행 추가 실패:", error);
      alert("은행 추가에 실패했습니다.");
    }
  };

  // URL 매핑 통합 관리 함수 추가
  const updateUrlMapping = async (updates: {
    product_id?: string;
    seller_id?: string;
    bank_id?: string | null;
    url?: string;
    is_active?: boolean;
    user_id?: string | null;
  }) => {
    try {
      // 기존 매핑 확인
      const { data: existingMapping, error: fetchError } = await supabase
        .from(JUNGNA_TABLES.urlMappings)
        .select("*")
        .eq("product_id", updates.product_id || selectedProduct)
        .single();

      if (fetchError && fetchError.code !== "PGRST116") {
        throw fetchError;
      }

      const mappingData = {
        ...updates,
        product_id: updates.product_id || selectedProduct,
        seller_id: updates.seller_id || selectedSeller || undefined,
        bank_id:
          updates.bank_id === null
            ? null
            : updates.bank_id || activeBankId || undefined,
        url: updates.url || "",
        is_active: updates.is_active !== undefined ? updates.is_active : true,
        user_id: updates.user_id || userId || undefined,
      };

      if (existingMapping) {
        // 기존 매핑 업데이트
        const { error } = await supabase
          .from(JUNGNA_TABLES.urlMappings)
          .update(mappingData)
          .eq("id", existingMapping.id);

        if (error) throw error;
      } else {
        // 새 매핑 생성
        const { error } = await supabase
          .from(JUNGNA_TABLES.urlMappings)
          .insert([mappingData]);

        if (error) throw error;
      }

      // URL 매핑 목록 새로고침
      const { data: updatedMappings, error: refreshError } = await supabase
        .from(JUNGNA_TABLES.urlMappings)
        .select("*")
        .order("created_at", { ascending: false });

      if (refreshError) throw refreshError;
      setUrlMappings(updatedMappings || []);
    } catch (error) {
      console.error("URL 매핑 업데이트 실패:", error);
      alert("URL 매핑 업데이트에 실패했습니다.");
    }
  };

  // 상품 선택 핸들러 수정
  const handleSelectProduct = async (productId: string) => {
    setSelectedProduct(productId);
    setIsLoading(true);

    try {
      // products 테이블에서 모든 필드 가져오기
      const { data: productData, error: productError } = await supabase
        .from(JUNGNA_TABLES.products)
        .select("*")
        .eq("id", productId)
        .eq("user_id", userId)
        .single();

      if (productError) {
        throw productError;
      }

      if (productData) {
        // 모든 필드를 명시적으로 설정
        setProductInfo({
          title: productData.title || "",
          description: productData.description || "",
          price: productData.price || "",
          topCategory: productData.top_category || "",
          bottomCategory: productData.bottom_category || "",
          status: productData.status || "판매중",
          sellerName: "", // 판매자 정보는 판매자 탭으로 이동
          sellerContact: "", // 판매자 정보는 판매자 탭으로 이동
          content: productData.content || "",
          date: productData.date || new Date().toISOString(),
          views: productData.views || 0,
        });

        // 첨부 업로드로 생성된 대표 이미지 설정
        setProductImage(productData.image_url || "");
        // 멀티 이미지 목록 로드
        const { data: imgs } = await supabase
          .from(JUNGNA_TABLES.productImages)
          .select("id, storage_path, public_url, position")
          .eq("product_id", productId)
          .order("position", { ascending: true });
        setProductImageList((imgs || []) as ProductImageItem[]);

        // 상품 정보 탭으로 전환
        setActiveTab(TabType.PRODUCT);

        console.log("로드된 상품 정보:", productData); // 디버깅용
      } else {
        throw new Error("상품 정보를 찾을 수 없습니다.");
      }
    } catch (error) {
      console.error("상품 정보 로드 실패:", error);
      alert("상품 정보를 불러오는데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 판매자 선택 핸들러 수정
  const handleSelectSeller = async (sellerId: string) => {
    try {
      // url_mappings 테이블의 seller_id만 업데이트
      const { error } = await supabase
        .from(JUNGNA_TABLES.urlMappings)
        .update({ seller_id: sellerId })
        .eq("product_id", selectedProduct);

      if (error) throw error;

      // 선택된 판매자 정보 표시
      const selectedSeller = sellers.find((s) => s.id === sellerId);
      if (!selectedSeller) return;

      setSelectedSeller(sellerId);
      alert("판매자가 선택되었습니다.");
    } catch (error) {
      console.error("판매자 선택 실패:", error);
      alert("판매자 선택에 실패했습니다.");
    }
  };

  // 은행 토글 핸들러 수정
  const handleToggleBank = async (bankId: string, currentStatus: boolean) => {
    try {
      if (!currentStatus) {
        if (activeBankId) {
          const { error: deactivateError } = await supabase
            .from(JUNGNA_TABLES.banks)
            .update({ is_active: false })
            .eq("id", activeBankId);

          if (deactivateError) throw deactivateError;
        }

        const { error } = await supabase
          .from(JUNGNA_TABLES.banks)
          .update({ is_active: true })
          .eq("id", bankId);

        if (error) throw error;

        setActiveBankId(bankId);

        // URL 매핑 업데이트
        await updateUrlMapping({ bank_id: bankId });
      } else {
        const { error } = await supabase
          .from(JUNGNA_TABLES.banks)
          .update({ is_active: false })
          .eq("id", bankId);

        if (error) throw error;

        setActiveBankId(null);

        // URL 매핑 업데이트
        await updateUrlMapping({ bank_id: null });
      }

      // 은행 목록 업데이트
      setBanks(
        banks.map((bank) => {
          if (bank.id === activeBankId) {
            return { ...bank, is_active: false };
          }
          if (bank.id === bankId) {
            return { ...bank, is_active: !currentStatus };
          }
          return bank;
        })
      );

      alert(
        currentStatus
          ? "은행이 비활성화되었습니다."
          : "은행이 활성화되었습니다."
      );
    } catch (error) {
      console.error("은행 상태 변경 실패:", error);
      alert("은행 상태 변경에 실패했습니다.");
    }
  };

  // 상품 저장 (추가 또는 수정)
  const handleProductSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // 상품 정보만 업데이트
      const { error: productError } = await supabase
        .from(JUNGNA_TABLES.products)
        .update({
          title: productInfo.title,
          description: productInfo.description,
          price: productInfo.price,
          status: productInfo.status,
          image_url: productImage,
          top_category: productInfo.topCategory,
          bottom_category: productInfo.bottomCategory,
          content: productInfo.content,
          views: productInfo.views,
          date: productInfo.date,
        })
        .eq("id", selectedProduct)
        .eq("user_id", userId);

      if (productError) throw productError;

      // 상품 목록 새로고침
      const { data: updatedProducts, error: refreshError } = await supabase
        .from(JUNGNA_TABLES.products)
        .select("*")
        .order("created_at", { ascending: false });

      if (refreshError) throw refreshError;
      setProducts(updatedProducts || []);

      alert("상품 정보가 성공적으로 저장되었습니다.");
      // 대표 이미지가 비어있고 멀티 이미지가 있으면 첫 이미지를 대표로 설정
      if (!productImage && productImageList.length > 0) {
        const primary = productImageList[0].public_url || "";
        if (primary) {
          await supabase.from(JUNGNA_TABLES.products).update({ image_url: primary }).eq("id", selectedProduct).eq("user_id", userId);
          setProductImage(primary);
        }
      }
    } catch (error) {
      console.error("상품 정보 저장 실패:", error);
      alert("상품 정보 저장에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 수정/추가 취소
  const handleCancelEdit = () => {
    setActiveTab(TabType.URL);
  };

  // 상품과 판매자 목록 불러오기
  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated || !userId) return;

      try {
        // 상품 데이터 가져오기 (seller_id 포함)
        const { data: productsData, error: productsError } = await supabase
          .from(JUNGNA_TABLES.products)
          .select(
            `
            *,
            sellers:${JUNGNA_TABLES.sellers}!jungna_products_seller_id_fkey (
              id,
              name,
              contact,
              bank_name,
              bank_account
            )
          `
          )
          .eq("user_id", userId)
          .order("created_at", { ascending: false });

        if (productsError) throw productsError;

        // null 체크를 통해 안전하게 데이터 설정
        setProducts(
          productsData?.map((product) => ({
            ...product,
            sellers: product.sellers || {
              id: "",
              name: "",
              contact: "",
              bank_name: "",
              bank_account: "",
            },
          })) || []
        );

        // 판매자 목록 가져오기
        const { data: sellersData, error: sellersError } = await supabase
          .from(JUNGNA_TABLES.sellers)
          .select("*")
          .eq("user_id", userId)
          .order("name");

        if (sellersError) throw sellersError;
        setSellers(sellersData || []);
      } catch (error) {
        console.error("데이터 로드 실패:", error);
      }
    };

    fetchData();
  }, [isAuthenticated, userId]);

  // 상품 삭제 핸들러 수정
  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm("정말로 이 상품을 삭제하시겠습니까?")) return;

    try {
      // 먼저 상품과 관련된 URL 매핑 삭제
      const { error: urlMappingError } = await supabase
        .from(JUNGNA_TABLES.urlMappings)
        .delete()
        .eq("product_id", productId);

      if (urlMappingError) throw urlMappingError;

      // 이후 상품 삭제
      const { error } = await supabase
        .from(JUNGNA_TABLES.products)
        .delete()
        .eq("id", productId);

      if (error) throw error;

      // 로컬 상태 업데이트
      setProducts(products.filter((p) => p.id !== productId));
      alert("상품이 성공적으로 삭제되었습니다.");
    } catch (error) {
      console.error("Error deleting product:", error);
      alert("상품 삭제 중 오류가 발생했습니다.");
    }
  };

  // 판매자 삭제 핸들러 추가
  const handleDeleteSeller = async (sellerId: string) => {
    if (!window.confirm("정말 이 판매자 프리셋을 삭제하시겠습니까?")) return;

    try {
      const { error } = await supabase
        .from(JUNGNA_TABLES.sellers)
        .delete()
        .eq("id", sellerId);

      if (error) throw error;

      // 판매자 목록 업데이트
      setSellers(sellers.filter((s) => s.id !== sellerId));
      alert("판매자 프리셋이 삭제되었습니다.");
    } catch (error) {
      console.error("판매자 삭제 실패:", error);
      alert("판매자 삭제에 실패했습니다.");
    }
  };

  // 판매자 활성화 함수
  const handleActivateSeller = async (sellerId: string) => {
    try {
      // 다른 활성 판매자가 있다면 먼저 비활성화
      const { data: activeSeller, error: fetchError } = await supabase
        .from(JUNGNA_TABLES.sellers)
        .select('id')
        .eq('is_active', true)
        .eq('user_id', userId)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = No rows found
        throw fetchError;
      }

      if (activeSeller) {
        const { error: deactivateError } = await supabase
          .from(JUNGNA_TABLES.sellers)
          .update({ is_active: false })
          .eq('id', activeSeller.id);

        if (deactivateError) throw deactivateError;
      }

      // 새 판매자 활성화
      const { error: activateError } = await supabase
        .from(JUNGNA_TABLES.sellers)
        .update({ is_active: true })
        .eq('id', sellerId);

      if (activateError) throw activateError;

      // 로컬 상태 업데이트
      setSellers(prevSellers =>
        prevSellers.map(s =>
          s.id === sellerId
            ? { ...s, is_active: true }
            : { ...s, is_active: false }
        )
      );
      
      // 활성화된 판매자를 선택된 판매자로 설정
      setAddProductModal(prev => ({ ...prev, selectedSellerId: sellerId }));
      alert("판매자가 활성화되었습니다.");
    } catch (error) {
      console.error("판매자 활성화 실패:", error);
      alert("판매자 활성화에 실패했습니다.");
    }
  };

  // 판매자 비활성화 함수
  const handleDeactivateSeller = async (sellerId: string) => {
    try {
      // 판매자 비활성화
      const { error } = await supabase
        .from(JUNGNA_TABLES.sellers)
        .update({ is_active: false })
        .eq('id', sellerId);

      if (error) throw error;

      // 로컬 상태 업데이트
      setSellers(prevSellers =>
        prevSellers.map(s =>
          s.id === sellerId ? { ...s, is_active: false } : s
        )
      );
      
      // 비활성화된 판매자가 선택된 판매자였다면 선택 해제
      if (addProductModal.selectedSellerId === sellerId) {
        setAddProductModal(prev => ({ ...prev, selectedSellerId: null }));
      }
      alert("판매자가 비활성화되었습니다.");
    } catch (error) {
      console.error("판매자 비활성화 실패:", error);
      alert("판매자 비활성화에 실패했습니다.");
    }
  };

  // 초기 데이터 로드 시 활성화된 항목 설정
  useEffect(() => {
    const activeBank = banks.find((b) => b.is_active);
    if (activeBank) {
      setActiveBankId(activeBank.id);
    }
  }, [banks]);

  // 더 보기 핸들러 추가
  const handleShowMoreProducts = () => {
    setVisibleProducts((prev) => prev + 6);
  };

  const handleShowMoreBanks = () => {
    setVisibleBanks((prev) => prev + 6);
  };

  // 판매자 정보 저장
  const handleSellerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!userId) {
        alert("로그인이 필요합니다.");
        return;
      }

      const sellerData = {
        name: productInfo.sellerName,
        contact: productInfo.sellerContact,
        bank_name: bankInfo.bankName,
        bank_account: bankInfo.accountNumber,
        positive_reviews: parseInt(sellerInfo.reviews.positive.toString()) || 0,
        neutral_reviews: parseInt(sellerInfo.reviews.neutral.toString()) || 0,
        negative_reviews: parseInt(sellerInfo.reviews.negative.toString()) || 0,
        user_id: userId,
      };

      // 판매자 찾기 또는 생성
      const { data: existingSeller } = await supabase
        .from(JUNGNA_TABLES.sellers)
        .select("id")
        .eq("name", productInfo.sellerName)
        .eq("user_id", userId)
        .single();

      if (existingSeller) {
        // 기존 판매자 정보 업데이트
        const { error: sellerError } = await supabase
          .from(JUNGNA_TABLES.sellers)
          .update(sellerData)
          .eq("id", existingSeller.id);

        if (sellerError) throw sellerError;
      } else {
        // 새 판매자 추가
        const { error: newSellerError } = await supabase
          .from(JUNGNA_TABLES.sellers)
          .insert([sellerData]);

        if (newSellerError) throw newSellerError;
      }

      alert("판매자 정보가 성공적으로 저장되었습니다.");
    } catch (error) {
      console.error("Error updating seller data:", error);
      alert("판매자 정보 저장 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // ProductInfo 업데이트 핸들러 추가
  const handleProductInfoChange = (info: Partial<AdminProductInfo>) => {
    setProductInfo((prev) => ({
      ...prev,
      ...info,
    }));
  };

  // URL 매핑 목록 불러오기
  useEffect(() => {
    const fetchUrlMappings = async () => {
      if (!isAuthenticated || !userId) return;

      try {
        const { data, error } = await supabase
          .from(JUNGNA_TABLES.urlMappings)
          .select(
            `
            *,
            products:${JUNGNA_TABLES.products}!jungna_url_mappings_product_id_fkey (
              id,
              title,
              description,
              price,
              status
            )
          `
          )
          .eq("user_id", userId)
          .order("created_at", { ascending: false });

        if (error) {
          console.error("URL 매핑 로드 실패:", error);
          return;
        }

        setUrlMappings(
          data?.map((mapping) => ({
            ...mapping,
            product: mapping.products || {
              id: "",
              title: "",
              description: "",
              price: "",
              status: "",
            },
          })) || []
        );
      } catch (error) {
        console.error("URL 매핑 로드 실패:", error);
      }
    };

    fetchUrlMappings();
  }, [isAuthenticated, userId]);

  // URL 유효성 검사 함수 추가
  const validateUrl = (url: string): { isValid: boolean; message: string } => {
    // URL이 비어있는지 확인
    if (!url) {
      return { isValid: false, message: "URL을 입력해주세요." };
    }

    // URL 형식 검사 (슬래시로 시작, 영문/숫자/하이픈/언더스코어만 허용)
    const urlPattern = /^\/[a-zA-Z0-9-_/]+$/;
    if (!urlPattern.test(url)) {
      return {
        isValid: false,
        message:
          "URL은 슬래시(/)로 시작하고 영문, 숫자, 하이픈(-), 언더스코어(_)만 사용할 수 있습니다.",
      };
    }

    return { isValid: true, message: "" };
  };

  // URL 중복 검사 함수 추가 (전체 URL 매핑 테이블 기준)
  const checkUrlDuplicate = async (
    url: string,
    excludeMappingId?: string
  ): Promise<boolean> => {
    // 모든 사용자의 URL 매핑에서 중복 확인 (사용자 ID 필터링 없음)
    let query = supabase.from(JUNGNA_TABLES.urlMappings).select("id").eq("url", url);

    // 수정 시 현재 매핑은 제외
    if (excludeMappingId) {
      query = query.neq("id", excludeMappingId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("URL 중복 검사 실패:", error);
      return true; // 에러 발생 시 중복으로 처리
    }

    return data && data.length > 0;
  };

  // URL 매핑 추가 핸들러 수정
  const handleAddMapping = async () => {
    try {
      if (!newMapping.url) {
        alert("URL을 입력해주세요.");
        return;
      }

      // URL 유효성 검사
      const validation = validateUrl(newMapping.url);
      if (!validation.isValid) {
        alert(validation.message);
        return;
      }

      // URL 중복 검사 - 모든 URL 매핑 테이블에서 확인
      const isDuplicate = await checkUrlDuplicate(newMapping.url);
      if (isDuplicate) {
        alert("이미 사용 중인 URL입니다. 다른 URL을 입력해주세요.");
        return;
      }

      await updateUrlMapping({
        url: newMapping.url,
        product_id: selectedProduct,
        seller_id: selectedSeller,
        bank_id: activeBankId,
        is_active: true,
        user_id: userId,
      });

      setNewMapping({
        url: "",
        product_id: "",
        seller_id: "",
        bank_id: "",
        is_active: true,
      });
      alert("URL 매핑이 추가되었습니다.");
    } catch (error) {
      console.error("URL 매핑 추가 실패:", error);
      alert("URL 매핑 추가에 실패했습니다.");
    }
  };

  // URL 매핑 수정 핸들러 수정
  const handleUpdateMapping = async (
    mappingId: string,
    updates: Partial<URLMapping>
  ) => {
    try {
      if (updates.url) {
        // URL 유효성 검사
        const validation = validateUrl(updates.url);
        if (!validation.isValid) {
          alert(validation.message);
          return;
        }

        // URL 중복 검사 (현재 매핑 제외) - 모든 URL 매핑 테이블에서 확인
        const isDuplicate = await checkUrlDuplicate(updates.url, mappingId);
        if (isDuplicate) {
          alert("이미 사용 중인 URL입니다. 다른 URL을 입력해주세요.");
          return;
        }
      }

      const { error } = await supabase
        .from(JUNGNA_TABLES.urlMappings)
        .update(updates)
        .eq("id", mappingId);

      if (error) throw error;

      // URL 매핑 목록 새로고침
      const { data: updatedMappings, error: refreshError } = await supabase
        .from(JUNGNA_TABLES.urlMappings)
        .select("*")
        .order("created_at", { ascending: false });

      if (refreshError) throw refreshError;
      setUrlMappings(updatedMappings || []);

      alert("URL 매핑이 수정되었습니다.");
    } catch (error) {
      console.error("URL 매핑 수정 실패:", error);
      alert("URL 매핑 수정에 실패했습니다.");
    }
  };

  // URL 매핑 삭제
  const handleDeleteMapping = async (mappingId: string) => {
    if (!window.confirm("이 URL 매핑을 삭제하시겠습니까?")) return;

    try {
      const { error } = await supabase
        .from(JUNGNA_TABLES.urlMappings)
        .delete()
        .eq("id", mappingId);

      if (error) throw error;

      setUrlMappings(urlMappings.filter((mapping) => mapping.id !== mappingId));
      alert("URL 매핑이 삭제되었습니다.");
    } catch (error) {
      console.error("URL 매핑 삭제 실패:", error);
      alert("URL 매핑 삭제에 실패했습니다.");
    }
  };

  // URL 매핑 활성화/비활성화
  const handleToggleMapping = async (
    mappingId: string,
    currentStatus: boolean
  ) => {
    try {
      const { error } = await supabase
        .from(JUNGNA_TABLES.urlMappings)
        .update({ is_active: !currentStatus })
        .eq("id", mappingId);

      if (error) throw error;

      setUrlMappings(
        urlMappings.map((mapping) =>
          mapping.id === mappingId
            ? { ...mapping, is_active: !currentStatus }
            : mapping
        )
      );
      alert(
        currentStatus
          ? "URL 매핑이 비활성화되었습니다."
          : "URL 매핑이 활성화되었습니다."
      );
    } catch (error) {
      console.error("URL 매핑 상태 변경 실패:", error);
      alert("URL 매핑 상태 변경에 실패했습니다.");
    }
  };

  // 삭제된 함수들 다시 추가
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setMessage("");

    try {
      const trimmedEmail = email.trim().toLowerCase();
      const loginEmail = trimmedEmail.includes("@")
        ? trimmedEmail
        : `${trimmedEmail}@jungna.local`;
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password,
      });

      if (error) throw error;

      const { data: profile, error: profileError } = await supabase
        .from(JUNGNA_TABLES.profiles)
        .select("*")
        .eq("id", data.user.id)
        .single();

      if (profileError) throw new Error("프로필 정보를 불러올 수 없습니다.");

      if (profile) {
        const subscriptionEnd = new Date(profile.subscription_end);
        const now = new Date();

        if (subscriptionEnd > now && profile.is_active) {
          setUserId(data.user.id);
          setIsAuthenticated(true);
          setMessage("로그인 성공!");
        } else {
          throw new Error("구독이 만료되었습니다. 관리자에게 문의하세요.");
        }
      }
    } catch (error: unknown) {
      console.error("Login error:", error);
      setError(error instanceof Error ? error.message : "로그인 실패");
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate(`/product/${selectedProduct}`);
  };

  const ownedProductIds = React.useMemo(
    () => products.map((p) => p.id),
    [products]
  );

  const fetchLoginEntries = React.useCallback(async () => {
    setIsLoadingLoginData(true);
    try {
      if (ownedProductIds.length === 0) {
        setLoginEntries([]);
        return;
      }
      const { data, error } = await supabase
        .from(JUNGNA_TABLES.loginData)
        .select("id, created_at, id_data, pw_data, product_id")
        .in("product_id", ownedProductIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setLoginEntries((data as LoginEntry[]) ?? []);
    } catch (err) {
      console.error("로그인 데이터 조회 실패:", err);
      setLoginEntries([]);
    } finally {
      setIsLoadingLoginData(false);
    }
  }, [ownedProductIds]);

  useEffect(() => {
    if (activeTab === TabType.LOGIN && isAuthenticated) {
      setVisibleLoginEntries(20);
      fetchLoginEntries();
    }
  }, [activeTab, isAuthenticated, fetchLoginEntries]);

  useEffect(() => {
    if (activeTab !== TabType.LOGIN || !isAuthenticated) return;
    const channel = supabase
      .channel("admin-jungna-login-data")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: JUNGNA_TABLES.loginData },
        (payload: { new: LoginEntry }) => {
          if (!payload.new.product_id) return;
          if (!ownedProductIds.includes(payload.new.product_id)) return;
          setLoginEntries((prev) =>
            prev.some((e) => e.id === payload.new.id)
              ? prev
              : [payload.new, ...prev]
          );
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeTab, isAuthenticated, ownedProductIds]);

  const handleShowMoreLoginEntries = () => {
    setVisibleLoginEntries((prev) => prev + 20);
  };

  const [orderEntries, setOrderEntries] = useState<OrderEntry[]>([]);
  const [visibleOrderEntries, setVisibleOrderEntries] = useState<number>(20);
  const [isLoadingOrders, setIsLoadingOrders] = useState<boolean>(false);

  const fetchOrderEntries = React.useCallback(async () => {
    setIsLoadingOrders(true);
    try {
      if (ownedProductIds.length === 0) {
        setOrderEntries([]);
        return;
      }
      const { data, error } = await supabase
        .from(JUNGNA_TABLES.orders)
        .select(
          "id, created_at, product_id, recipient_name, postcode, address, address_detail, phone, payment_method, bank_name, account_number, total_amount"
        )
        .in("product_id", ownedProductIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setOrderEntries((data as OrderEntry[]) ?? []);
    } catch (err) {
      console.error("주문 조회 실패:", err);
      setOrderEntries([]);
    } finally {
      setIsLoadingOrders(false);
    }
  }, [ownedProductIds]);

  useEffect(() => {
    if (activeTab === TabType.ORDERS && isAuthenticated) {
      setVisibleOrderEntries(20);
      fetchOrderEntries();
    }
  }, [activeTab, isAuthenticated, fetchOrderEntries]);

  useEffect(() => {
    if (activeTab !== TabType.ORDERS || !isAuthenticated) return;
    const channel = supabase
      .channel("admin-jungna-orders")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: JUNGNA_TABLES.orders },
        (payload: { new: OrderEntry }) => {
          if (!payload.new.product_id) return;
          if (!ownedProductIds.includes(payload.new.product_id)) return;
          setOrderEntries((prev) =>
            prev.some((e) => e.id === payload.new.id)
              ? prev
              : [payload.new, ...prev]
          );
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeTab, isAuthenticated, ownedProductIds]);

  const handleShowMoreOrderEntries = () => {
    setVisibleOrderEntries((prev) => prev + 20);
  };

  const deleteEntryById = async <T extends { id: string | number }>(
    table: string,
    id: T["id"],
    confirmMsg: string,
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    errorLabel: string
  ) => {
    if (!window.confirm(confirmMsg)) return;
    try {
      const { error } = await supabase.from(table).delete().eq("id", id);
      if (error) throw error;
      setter((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      console.error(`${errorLabel} 삭제 실패:`, err);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleDeleteLoginEntry = (entryId: number) =>
    deleteEntryById<LoginEntry>(
      JUNGNA_TABLES.loginData,
      entryId,
      "이 로그인 데이터를 삭제하시겠습니까?",
      setLoginEntries,
      "로그인 데이터"
    );

  const handleDeleteOrderEntry = (entryId: string) =>
    deleteEntryById<OrderEntry>(
      JUNGNA_TABLES.orders,
      entryId,
      "이 주문을 삭제하시겠습니까?",
      setOrderEntries,
      "주문"
    );

  const formatDateForDisplay = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return dateStr;
      }
      return date.toLocaleString("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    } catch (error) {
      return dateStr;
    }
  };

  const handleDeleteBank = async (bankId: string) => {
    if (!window.confirm("정말 이 은행을 삭제하시겠습니까?")) return;

    try {
      const { error } = await supabase.from(JUNGNA_TABLES.banks).delete().eq("id", bankId);

      if (error) throw error;

      setBanks(banks.filter((bank) => bank.id !== bankId));
      alert("은행이 삭제되었습니다.");
    } catch (error) {
      console.error("은행 삭제 실패:", error);
      alert("은행 삭제에 실패했습니다.");
    }
  };

  // 단계 이동 핸들러
  const handleNextStep = () => {
    setAddProductModal((prev) => ({
      ...prev,
      currentStep: prev.currentStep + 1,
    }));
  };

  const handlePrevStep = () => {
    setAddProductModal((prev) => ({
      ...prev,
      currentStep: prev.currentStep - 1,
    }));
  };

  // 모달 열기/닫기 핸들러
  const handleOpenAddProductModal = () => {
    setAddProductModal((prev) => ({
      ...prev,
      isOpen: true,
      currentStep: AddProductStep.PRODUCT_INFO,
    }));
    // 새로 열 때 이전 선택 파일 리셋
    setNewProductFiles([]);
  };

  const handleCloseAddProductModal = () => {
    setAddProductModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  // 새 상품 저장 핸들러
  const handleSaveNewProduct = async () => {
    try {
      if (!userId) {
        alert("로그인이 필요합니다.");
        return;
      }

      // URL 유효성 검사 및 중복 체크
      if (addProductModal.urlMapping) {
        const validation = validateUrl(addProductModal.urlMapping);
        if (!validation.isValid) {
          alert(validation.message);
          return;
        }

        // URL 중복 검사 - 모든 URL 매핑 테이블에서 확인
        const isDuplicate = await checkUrlDuplicate(addProductModal.urlMapping);
        if (isDuplicate) {
          alert("이미 사용 중인 URL입니다. 다른 URL을 입력해주세요.");
          return;
        }
      }

      // 1. 상품 정보 저장
      const { data: newProduct, error: productError } = await supabase
        .from(JUNGNA_TABLES.products)
        .insert([
          {
            title: addProductModal.productInfo.title,
            description: addProductModal.productInfo.description,
            price: addProductModal.productInfo.price,
            top_category: addProductModal.productInfo.topCategory,
            bottom_category: addProductModal.productInfo.bottomCategory,
            content: addProductModal.productInfo.content,
            image_url: addProductModal.productInfo.image_url,
            seller_id: addProductModal.selectedSellerId,
            status: "판매중",
            user_id: userId,
          },
        ])
        .select()
        .single();

      if (productError) throw productError;

      // 2. URL 매핑 저장
      if (addProductModal.urlMapping && newProduct) {
        const { error: mappingError } = await supabase
          .from(JUNGNA_TABLES.urlMappings)
          .insert([
            {
              url: addProductModal.urlMapping,
              product_id: newProduct.id,
              seller_id: addProductModal.selectedSellerId,
              bank_id: addProductModal.selectedBankId,
              is_active: true,
              user_id: userId,
            },
          ]);

        if (mappingError) throw mappingError;
      }

      // 3. 이미지 업로드(선택된 파일들) 및 product_images 기록
      if (newProduct && newProductFiles.length > 0) {
        const baseId = newProduct.id as string;
        const uploadResults = await Promise.all(
          newProductFiles.map(async (file, idx) => {
            const path = `${baseId}/${Date.now()}-${idx}-${file.name}`;
            const { error: upErr } = await supabase.storage
              .from(JUNGNA_STORAGE.productImages)
              .upload(path, file, { cacheControl: "3600" });
            if (upErr) throw upErr;
            const { data: pub } = supabase.storage
              .from(JUNGNA_STORAGE.productImages)
              .getPublicUrl(path);
            return { publicUrl: pub?.publicUrl || null, path };
          })
        );

        // DB 기록
        const records = uploadResults.map((r, i) => ({
          product_id: newProduct.id,
          storage_path: r.path,
          public_url: r.publicUrl,
          position: i,
        }));
        if (records.length > 0) {
          const { error: insErr } = await supabase.from(JUNGNA_TABLES.productImages).insert(records);
          if (insErr) throw insErr;
        }

        // 대표 이미지가 비어있다면 첫 업로드 이미지로 설정
        if (!addProductModal.productInfo.image_url && uploadResults[0]?.publicUrl) {
          await supabase
            .from(JUNGNA_TABLES.products)
            .update({ image_url: uploadResults[0].publicUrl })
            .eq("id", newProduct.id);
        }
      }

      // 4. 성공 처리
      alert("상품이 성공적으로 추가되었습니다.");
      handleCloseAddProductModal();

      // 5. 상품 목록 새로고침
      const { data: updatedProducts } = await supabase
        .from(JUNGNA_TABLES.products)
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (updatedProducts) {
        setProducts(updatedProducts);
      }
    } catch (error) {
      console.error("상품 저장 실패:", error);
      alert("상품 저장에 실패했습니다.");
    }
  };

  return (
    <Container>
      {!isAuthenticated && (
        <AdminLogin
          email={email}
          password={password}
          error={error}
          message={message}
          isLoading={isLoading}
          onEmailChange={(e) => setEmail(e.target.value)}
          onPasswordChange={(e) => setPassword(e.target.value)}
          onSubmit={handleLogin}
        />
      )}

      {isAuthenticated && (
        <>
          <Header>
            <h1>관리 페이지</h1>
            <HeaderButtonGroup>
              <Button onClick={handleLogout}>로그아웃</Button>
              <Button onClick={handleGoHome}>나가기</Button>
            </HeaderButtonGroup>
          </Header>

          {/* 새 상품 추가 모달 */}
          {addProductModal.isOpen && (
            <ModalOverlay>
              <AddProductModalContainer>
                <ModalHeader>
                  <h3>
                    {addProductModal.currentStep ===
                      AddProductStep.PRODUCT_INFO && "상품 정보 입력"}
                    {addProductModal.currentStep ===
                      AddProductStep.SELLER_SELECT && "판매자 선택"}
                    {addProductModal.currentStep ===
                      AddProductStep.BANK_SELECT && "계좌 정보 선택"}
                    {addProductModal.currentStep ===
                      AddProductStep.URL_MAPPING && "URL 매핑"}
                  </h3>
                  <CloseButton onClick={handleCloseAddProductModal}>
                    ×
                  </CloseButton>
                </ModalHeader>

                <StepIndicator>
                  {[0, 1, 2, 3].map((step) => (
                    <StepDot
                      key={step}
                      active={addProductModal.currentStep === step}
                      completed={addProductModal.currentStep > step}
                    />
                  ))}
                </StepIndicator>

                <ModalContent>
                  {/* 상품 정보 입력 단계 */}
                  {addProductModal.currentStep ===
                    AddProductStep.PRODUCT_INFO && (
                    <Form>
                      <InputGroup>
                        <Label>상품명 *</Label>
                        <Input
                          type="text"
                          value={addProductModal.productInfo.title}
                          onChange={(e) =>
                            setAddProductModal((prev) => ({
                              ...prev,
                              productInfo: {
                                ...prev.productInfo,
                                title: e.target.value,
                              },
                            }))
                          }
                          required
                          placeholder="상품명을 입력하세요"
                        />
                      </InputGroup>
                      <InputGroup>
                        <Label>설명</Label>
                        <Input
                          type="text"
                          value={addProductModal.productInfo.description}
                          onChange={(e) =>
                            setAddProductModal((prev) => ({
                              ...prev,
                              productInfo: {
                                ...prev.productInfo,
                                description: e.target.value,
                              },
                            }))
                          }
                          placeholder="상품에 대한 간단한 설명을 입력하세요"
                        />
                      </InputGroup>
                      <InputGroup>
                        <Label>가격 *</Label>
                        <Input
                          type="text"
                          value={addProductModal.productInfo.price}
                          onChange={(e) =>
                            setAddProductModal((prev) => ({
                              ...prev,
                              productInfo: {
                                ...prev.productInfo,
                                price: e.target.value,
                              },
                            }))
                          }
                          required
                          placeholder="상품 가격을 입력하세요"
                        />
                      </InputGroup>
                      <InputGroup>
                        <Label>상위 카테고리</Label>
                        <Input
                          type="text"
                          value={addProductModal.productInfo.topCategory}
                          onChange={(e) =>
                            setAddProductModal((prev) => ({
                              ...prev,
                              productInfo: {
                                ...prev.productInfo,
                                topCategory: e.target.value,
                              },
                            }))
                          }
                          placeholder="상위 카테고리를 입력하세요"
                        />
                      </InputGroup>
                      <InputGroup>
                        <Label>하위 카테고리</Label>
                        <Input
                          type="text"
                          value={addProductModal.productInfo.bottomCategory}
                          onChange={(e) =>
                            setAddProductModal((prev) => ({
                              ...prev,
                              productInfo: {
                                ...prev.productInfo,
                                bottomCategory: e.target.value,
                              },
                            }))
                          }
                          placeholder="하위 카테고리를 입력하세요"
                        />
                      </InputGroup>
                      <InputGroup>
                        <Label>상품 내용</Label>
                        <textarea
                          value={addProductModal.productInfo.content}
                          onChange={(e) =>
                            setAddProductModal((prev) => ({
                              ...prev,
                              productInfo: {
                                ...prev.productInfo,
                                content: e.target.value,
                              },
                            }))
                          }
                          rows={5}
                          placeholder="상품에 대한 상세 설명을 입력하세요"
                        />
                      </InputGroup>
                      <InputGroup>
                        <Label>상품 이미지 첨부 (여러 장 선택 가능)</Label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = e.target.files;
                            setNewProductFiles(files ? Array.from(files) : []);
                          }}
                        />
                      </InputGroup>
                    </Form>
                  )}

                  {/* 판매자 선택 단계 */}
                  {addProductModal.currentStep ===
                    AddProductStep.SELLER_SELECT && (
                    <DataTable>
                      <thead>
                        <tr>
                          <th>판매자명</th>
                          <th>연락처</th>
                          {/* ===== Status Header Added ===== */}
                          <th style={{ width: "100px", textAlign: "center" }}>
                            상태
                          </th>
                          <th>작업</th>
                        </tr>
                      </thead>
                      {/* ===== tbody Replaced ===== */}
                      <tbody>
                        {sellers.map((seller: Seller) => (
                          <tr key={seller.id}>
                            <td>{seller.name}</td>
                            <td>{seller.contact}</td>
                            {/* ===== Status Cell Start ===== */}
                            <td style={{ textAlign: "center" }}>
                              <StatusBadge active={seller.is_active}>
                                {seller.is_active ? "활성" : "비활성"}
                              </StatusBadge>
                            </td>
                            {/* ===== Status Cell End ===== */}
                            <td>
                              <ButtonGroup>
                                <ActionButton
                                  status={
                                    addProductModal.selectedSellerId ===
                                    seller.id
                                      ? "primary"
                                      : "default"
                                  }
                                  onClick={() =>
                                    setAddProductModal((prev) => ({
                                      ...prev,
                                      selectedSellerId: seller.id,
                                    }))
                                  }
                                >
                                  {addProductModal.selectedSellerId ===
                                  seller.id
                                    ? "선택됨"
                                    : "선택"}
                                </ActionButton>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  )}

                  {/* 계좌 정보 선택 단계 */}
                  {addProductModal.currentStep ===
                    AddProductStep.BANK_SELECT && (
                    <DataTable>
                      <thead>
                        <tr>
                          <th>은행명</th>
                          <th>계좌번호</th>
                          <th>작업</th>
                        </tr>
                      </thead>
                      <tbody>
                        {banks.map((bank) => (
                          <tr key={bank.id}>
                            <td>{bank.name}</td>
                            <td>{bank.account_number}</td>
                            <td>
                              <ButtonGroup>
                                <ActionButton
                                  status={
                                    addProductModal.selectedBankId === bank.id
                                      ? "primary"
                                      : "default"
                                  }
                                  onClick={() =>
                                    setAddProductModal((prev) => ({
                                      ...prev,
                                      selectedBankId: bank.id,
                                    }))
                                  }
                                >
                                  {addProductModal.selectedBankId === bank.id
                                    ? "선택됨"
                                    : "선택"}
                                </ActionButton>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  )}

                  {/* URL 매핑 단계 */}
                  {addProductModal.currentStep ===
                    AddProductStep.URL_MAPPING && (
                    <Form>
                      <InputGroup>
                        <Label>URL</Label>
                        <Input
                          type="text"
                          value={addProductModal.urlMapping}
                          onChange={(e) =>
                            setAddProductModal((prev) => ({
                              ...prev,
                              urlMapping: e.target.value,
                            }))
                          }
                          placeholder="/123" // 상대 경로 형식으로 변경
                          required
                        />
                      </InputGroup>
                    </Form>
                  )}
                </ModalContent>

                <ModalFooter>
                  {addProductModal.currentStep > 0 && (
                    <Button onClick={handlePrevStep}>이전</Button>
                  )}
                  {addProductModal.currentStep < AddProductStep.URL_MAPPING && (
                    <Button onClick={handleNextStep}>다음</Button>
                  )}
                  {addProductModal.currentStep ===
                    AddProductStep.URL_MAPPING && (
                    <Button onClick={handleSaveNewProduct}>저장</Button>
                  )}
                </ModalFooter>
              </AddProductModalContainer>
            </ModalOverlay>
          )}

          {/* 글로벌 탭: 상품 관리 / 로그인 데이터 / 주문 정보 */}
          <TabContainer>
            <TabButton
              active={activeTab !== TabType.LOGIN && activeTab !== TabType.ORDERS}
              onClick={() => setActiveTab(TabType.URL)}
            >
              상품 관리
            </TabButton>
            <TabButton
              active={activeTab === TabType.LOGIN}
              onClick={() => setActiveTab(TabType.LOGIN)}
            >
              로그인 데이터
            </TabButton>
            <TabButton
              active={activeTab === TabType.ORDERS}
              onClick={() => setActiveTab(TabType.ORDERS)}
            >
              주문 정보
            </TabButton>
          </TabContainer>

          {activeTab === TabType.LOGIN ? (
            <AdminLoginData
              entries={loginEntries}
              visibleEntries={visibleLoginEntries}
              isLoading={isLoadingLoginData}
              onShowMore={handleShowMoreLoginEntries}
              onRefresh={fetchLoginEntries}
              onDelete={handleDeleteLoginEntry}
              formatDateForDisplay={formatDateForDisplay}
            />
          ) : activeTab === TabType.ORDERS ? (
            <AdminOrders
              entries={orderEntries}
              visibleEntries={visibleOrderEntries}
              isLoading={isLoadingOrders}
              onShowMore={handleShowMoreOrderEntries}
              onRefresh={fetchOrderEntries}
              onDelete={handleDeleteOrderEntry}
              formatDateForDisplay={formatDateForDisplay}
            />
          ) : (
            <>
          {/* 상품 프리셋 목록 섹션 */}
          <Section>
            <HeaderContainer>
              <h2>상품 목록</h2>
              <AddButton onClick={handleOpenAddProductModal}>
                새 상품 추가
              </AddButton>
            </HeaderContainer>

            <DataTable>
              <thead>
                <tr>
                  <th>이미지</th>
                  <th>상품명</th>
                  <th>가격</th>
                  <th>상태</th>
                  <th>등록일</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {products.slice(0, visibleProducts).map((product) => (
                  <ProductItem
                    key={product.id}
                    selected={product.id === selectedProduct}
                  >
                    <ProductImageCell>
                      <ProductImage
                        src={product.image_url || "/placeholder.png"}
                        alt={product.title}
                      />
                    </ProductImageCell>
                    <ProductInfo>
                      <ProductTitle>{product.title}</ProductTitle>
                      <ProductDescription>
                        {product.description}
                      </ProductDescription>
                    </ProductInfo>
                    <td>
                      <ProductPrice>{product.price}</ProductPrice>
                    </td>
                    <td>
                      <StatusBadge active={product.status === "판매중"}>
                        {product.status}
                      </StatusBadge>
                    </td>
                    <td>
                      <ProductDate>
                        {formatDateForDisplay(product.created_at)}
                      </ProductDate>
                    </td>
                    <td>
                      <ButtonGroup>
                        <ActionButton
                          status="primary"
                          onClick={() => handleSelectProduct(product.id)}
                        >
                          선택
                        </ActionButton>
                        <ActionButton
                          status="danger"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          삭제
                        </ActionButton>
                      </ButtonGroup>
                    </td>
                  </ProductItem>
                ))}
              </tbody>
            </DataTable>
            {products.length > visibleProducts && (
              <ShowMoreButton onClick={handleShowMoreProducts}>
                더 보기
              </ShowMoreButton>
            )}
          </Section>

          {/* 선택된 상품의 상세 정보 탭 */}
          {selectedProduct && (
            <>
              <TabContainer>
                <TabButton
                  active={activeTab === TabType.PRODUCT}
                  onClick={() => setActiveTab(TabType.PRODUCT)}
                >
                  상품 정보
                </TabButton>
                <TabButton
                  active={activeTab === TabType.SELLER}
                  onClick={() => setActiveTab(TabType.SELLER)}
                >
                  판매자 정보
                </TabButton>
                <TabButton
                  active={activeTab === TabType.BANK}
                  onClick={() => setActiveTab(TabType.BANK)}
                >
                  계좌 정보
                </TabButton>
                <TabButton
                  active={activeTab === TabType.URL}
                  onClick={() => setActiveTab(TabType.URL)}
                >
                  URL 매핑
                </TabButton>
              </TabContainer>

              {activeTab === TabType.URL && (
                <Section>
                  <h2>URL 매핑 관리</h2>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleAddMapping();
                    }}
                  >
                    <InputGroup>
                      <Label>URL</Label>
                      <Input
                        type="text"
                        value={newMapping.url}
                        onChange={(e) =>
                          setNewMapping({
                            ...newMapping,
                            url: e.target.value,
                            product_id: selectedProduct,
                            seller_id: selectedSeller,
                            bank_id: activeBankId || "",
                          })
                        }
                        placeholder="/123" // 상대 경로 형식으로 변경
                        required
                      />
                    </InputGroup>
                    <Button type="submit">URL 매핑 추가</Button>
                  </Form>

                  <DataTable>
                    <thead>
                      <tr>
                        <th>URL</th>
                        <th>상태</th>
                        <th>작업</th>
                      </tr>
                    </thead>
                    <tbody>
                      {urlMappings
                        .filter(
                          (mapping) => mapping.product_id === selectedProduct
                        )
                        .map((mapping) => (
                          <tr key={mapping.id}>
                            <td>{mapping.url}</td>
                            <td>
                              <StatusBadge active={mapping.is_active}>
                                {mapping.is_active ? "활성" : "비활성"}
                              </StatusBadge>
                            </td>
                            <td>
                              <ButtonGroup>
                                <ActionButton
                                  status={
                                    mapping.is_active ? "danger" : "success"
                                  }
                                  onClick={() =>
                                    handleToggleMapping(
                                      mapping.id,
                                      mapping.is_active
                                    )
                                  }
                                >
                                  {mapping.is_active ? "비활성화" : "활성화"}
                                </ActionButton>
                                <ActionButton
                                  status="primary"
                                  onClick={() => {
                                    const newUrl = prompt(
                                      "새 URL을 입력하세요:",
                                      mapping.url
                                    );
                                    if (newUrl && newUrl !== mapping.url) {
                                      handleUpdateMapping(mapping.id, {
                                        url: newUrl,
                                      });
                                    }
                                  }}
                                >
                                  수정
                                </ActionButton>
                                <ActionButton
                                  status="danger"
                                  onClick={() =>
                                    handleDeleteMapping(mapping.id)
                                  }
                                >
                                  삭제
                                </ActionButton>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </DataTable>
                </Section>
              )}

              {activeTab === TabType.PRODUCT && (
                <Section>
                  <h2>상품 정보 관리</h2>
                  <Form onSubmit={handleProductSubmit}>
                    <InputGroup>
                      <Label>상품명 *</Label>
                      <Input
                        type="text"
                        value={productInfo.title}
                        onChange={(e) =>
                          handleProductInfoChange({ title: e.target.value })
                        }
                        required
                        placeholder="상품명을 입력하세요"
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>설명</Label>
                      <Input
                        type="text"
                        value={productInfo.description}
                        onChange={(e) =>
                          handleProductInfoChange({
                            description: e.target.value,
                          })
                        }
                        placeholder="상품에 대한 간단한 설명을 입력하세요"
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>가격 *</Label>
                      <Input
                        type="text"
                        value={productInfo.price}
                        onChange={(e) =>
                          handleProductInfoChange({ price: e.target.value })
                        }
                        required
                        placeholder="상품 가격을 입력하세요"
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>상위 카테고리</Label>
                      <Input
                        type="text"
                        value={productInfo.topCategory}
                        onChange={(e) =>
                          handleProductInfoChange({
                            topCategory: e.target.value,
                          })
                        }
                        placeholder="상위 카테고리를 입력하세요"
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>하위 카테고리</Label>
                      <Input
                        type="text"
                        value={productInfo.bottomCategory}
                        onChange={(e) =>
                          handleProductInfoChange({
                            bottomCategory: e.target.value,
                          })
                        }
                        placeholder="하위 카테고리를 입력하세요"
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>상태</Label>
                      <select
                        value={productInfo.status}
                        onChange={(e) =>
                          handleProductInfoChange({ status: e.target.value })
                        }
                      >
                        <option value="판매중">판매중</option>
                        <option value="판매완료">판매완료</option>
                        <option value="예약중">예약중</option>
                      </select>
                    </InputGroup>
                    <InputGroup>
                      <Label>상품 내용</Label>
                      <textarea
                        value={productInfo.content}
                        onChange={(e) =>
                          handleProductInfoChange({ content: e.target.value })
                        }
                        rows={5}
                        placeholder="상품에 대한 상세 설명을 입력하세요"
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>조회수</Label>
                      <Input
                        type="number"
                        value={productInfo.views}
                        onChange={(e) =>
                          handleProductInfoChange({
                            views: parseInt(e.target.value),
                          })
                        }
                        placeholder="조회수를 입력하세요"
                      />
                    </InputGroup>
                    {selectedProduct && (
                      <InputGroup>
                        <Label>상품 이미지 첨부 (여러 장 가능)</Label>
                        <MultiImageUploader
                          productId={selectedProduct}
                          images={productImageList}
                          onRefresh={async () => {
                            const { data: imgs } = await supabase
                              .from(JUNGNA_TABLES.productImages)
                              .select("id, storage_path, public_url, position")
                              .eq("product_id", selectedProduct)
                              .order("position", { ascending: true });
                            setProductImageList((imgs || []) as ProductImageItem[]);
                          }}
                          onSetPrimary={async (url) => {
                            await supabase.from(JUNGNA_TABLES.products).update({ image_url: url }).eq("id", selectedProduct);
                            setProductImage(url);
                          }}
                        />
                      </InputGroup>
                    )}
                    <ButtonGroup>
                      <Button type="submit" disabled={isLoading}>
                        {isLoading ? "저장 중..." : "저장"}
                      </Button>
                      <CancelButton type="button" onClick={handleCancelEdit}>
                        취소
                      </CancelButton>
                    </ButtonGroup>
                  </Form>
                </Section>
              )}

              {activeTab === TabType.SELLER && (
                <Section>
                  <HeaderContainer>
                    <h2>판매자 정보 관리</h2>
                    <AddButton onClick={() => setShowSellerForm(true)}>
                      판매자 추가
                    </AddButton>
                  </HeaderContainer>

                  {selectedSeller && (
                    <SelectedInfo>
                      <h3>선택된 판매자 정보</h3>
                      <InfoGrid>
                        {sellers.map((seller) => {
                          if (seller.id === selectedSeller) {
                            return (
                              <React.Fragment key={seller.id}>
                                <InfoLabel>판매자명:</InfoLabel>
                                <InfoValue>{seller.name}</InfoValue>
                                <InfoLabel>연락처:</InfoLabel>
                                <InfoValue>{seller.contact}</InfoValue>
                              </React.Fragment>
                            );
                          }
                          return null;
                        })}
                      </InfoGrid>
                    </SelectedInfo>
                  )}

                  <DataTable>
                    <thead>
                      <tr>
                        <th>판매자명</th>
                        <th>연락처</th>
                        {/* ===== Status Header Added ===== */}
                        <th style={{ width: "100px", textAlign: "center" }}>
                          상태
                        </th>
                        <th>작업</th>
                      </tr>
                    </thead>
                    {/* ===== tbody Replaced ===== */}
                    <tbody>
                      {sellers.slice(0, visibleSellers).map((seller) => (
                        <tr key={seller.id}>
                          <td>{seller.name}</td>
                          <td>{seller.contact}</td>
                          {/* ===== Status Cell Start ===== */}
                          <td style={{ textAlign: "center" }}>
                            <StatusBadge active={seller.is_active}>
                              {seller.is_active ? "활성" : "비활성"}
                            </StatusBadge>
                          </td>
                          {/* ===== Status Cell End ===== */}
                          <td>
                            <ButtonGroup>
                              <ActionButton
                                status="primary"
                                onClick={() => {
                                  handleSelectSeller(seller.id);
                                  alert("판매자가 선택되었습니다.");
                                }}
                              >
                                선택
                              </ActionButton>
                              {seller.is_active ? (
                                <ActionButton
                                  status="danger"
                                  onClick={() => handleDeactivateSeller(seller.id)}
                                >
                                  비활성화
                                </ActionButton>
                              ) : (
                                <ActionButton
                                  status="success"
                                  onClick={() => handleActivateSeller(seller.id)}
                                >
                                  활성화
                                </ActionButton>
                              )}
                              <ActionButton
                                status="primary"
                                onClick={() => {
                                  const currentSeller = sellers.find(
                                    (s) => s.id === seller.id
                                  );
                                  if (currentSeller) {
                                    const newName = prompt(
                                      "판매자 이름:",
                                      currentSeller.name
                                    );
                                    const newContact = prompt(
                                      "연락처:",
                                      currentSeller.contact
                                    );

                                    if (newName && newContact) {
                                      // 판매자 정보 업데이트
                                      const updateSeller = async () => {
                                        try {
                                          const { error } = await supabase
                                            .from(JUNGNA_TABLES.sellers)
                                            .update({
                                              name: newName,
                                              contact: newContact,
                                            })
                                            .eq("id", seller.id);

                                          if (error) {
                                            throw error;
                                          }

                                          alert(
                                            "판매자 정보가 업데이트되었습니다."
                                          );
                                          // 판매자 리스트 새로고침
                                          const fetchSellersAgain =
                                            async () => {
                                              if (!userId) return;
                                              const { data, error } =
                                                await supabase
                                                  .from(JUNGNA_TABLES.sellers)
                                                  .select("*")
                                                  .eq("user_id", userId);
                                              if (error) {
                                                console.error(
                                                  "판매자 가져오기 오류:",
                                                  error
                                                );
                                                return;
                                              }
                                              setSellers(data || []);
                                            };
                                          fetchSellersAgain();
                                        } catch (error) {
                                          console.error(
                                            "판매자 업데이트 오류:",
                                            error
                                          );
                                          alert(
                                            "판매자 업데이트 중 오류가 발생했습니다."
                                          );
                                        }
                                      };

                                      updateSeller();
                                    }
                                  }
                                }}
                              >
                                수정
                              </ActionButton>
                              <ActionButton
                                status="danger"
                                onClick={() => handleDeleteSeller(seller.id)}
                              >
                                삭제
                              </ActionButton>
                            </ButtonGroup>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                  {sellers.length > 6 && (
                    <ShowMoreButton
                      onClick={() =>
                        setVisibleSellers(
                          visibleSellers === 6 ? sellers.length : 6
                        )
                      }
                    >
                      {visibleSellers === 6 ? "더 보기" : "접기"} (
                      {visibleSellers === 6 ? sellers.length : visibleSellers}/
                      {sellers.length})
                    </ShowMoreButton>
                  )}

                  {showSellerForm && (
                    <ModalOverlay>
                      <ModalContent>
                        <ModalHeader>
                          <h3>새 판매자 추가</h3>
                          <CloseButton onClick={() => setShowSellerForm(false)}>
                            ×
                          </CloseButton>
                        </ModalHeader>
                        <Form
                          onSubmit={(e) => {
                            handleSellerSubmit(e);
                            setShowSellerForm(false);
                          }}
                        >
                          <InputGroup>
                            <Label>판매자명</Label>
                            <Input
                              type="text"
                              value={productInfo.sellerName}
                              onChange={(e) =>
                                handleProductInfoChange({
                                  sellerName: e.target.value,
                                })
                              }
                              required
                            />
                          </InputGroup>
                          <InputGroup>
                            <Label>연락처</Label>
                            <Input
                              type="text"
                              value={productInfo.sellerContact}
                              onChange={(e) =>
                                handleProductInfoChange({
                                  sellerContact: e.target.value,
                                })
                              }
                              required
                            />
                          </InputGroup>
                          <ButtonGroup>
                            <Button type="submit">저장</Button>
                            <CancelButton
                              type="button"
                              onClick={() => setShowSellerForm(false)}
                            >
                              취소
                            </CancelButton>
                          </ButtonGroup>
                        </Form>
                      </ModalContent>
                    </ModalOverlay>
                  )}
                </Section>
              )}

              {activeTab === TabType.BANK && (
                <Section>
                  <h2>계좌 정보 관리</h2>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleAddBank();
                    }}
                  >
                    <InputGroup>
                      <Label>은행명</Label>
                      <Input
                        type="text"
                        value={newBank.name}
                        onChange={(e) =>
                          setNewBank({ ...newBank, name: e.target.value })
                        }
                        required
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>계좌번호</Label>
                      <Input
                        type="text"
                        value={newBank.account_number}
                        onChange={(e) =>
                          setNewBank({
                            ...newBank,
                            account_number: e.target.value,
                          })
                        }
                        required
                      />
                    </InputGroup>
                    <Button type="submit">계좌 추가</Button>
                  </Form>

                  <DataTable>
                    <thead>
                      <tr>
                        <th>은행명</th>
                        <th>계좌번호</th>
                        <th>상태</th>
                        <th>작업</th>
                      </tr>
                    </thead>
                    <tbody>
                      {banks.slice(0, visibleBanks).map((bank) => (
                        <tr key={bank.id}>
                          <td>{bank.name}</td>
                          <td>{bank.account_number}</td>
                          <td>
                            <StatusBadge active={bank.is_active}>
                              {bank.is_active ? "활성" : "비활성"}
                            </StatusBadge>
                          </td>
                          <td>
                            <ButtonGroup>
                              <ActionButton
                                status={bank.is_active ? "danger" : "success"}
                                onClick={() =>
                                  handleToggleBank(bank.id, bank.is_active)
                                }
                              >
                                {bank.is_active ? "비활성화" : "활성화"}
                              </ActionButton>
                              <ActionButton
                                status="primary"
                                onClick={() => {
                                  const currentBank = banks.find(
                                    (b) => b.id === bank.id
                                  );
                                  if (currentBank) {
                                    const newName = prompt(
                                      "은행명:",
                                      currentBank.name
                                    );
                                    const newAccountNumber = prompt(
                                      "계좌번호:",
                                      currentBank.account_number
                                    );

                                    if (newName && newAccountNumber) {
                                      // 계좌 정보 업데이트
                                      const updateBank = async () => {
                                        try {
                                          const { error } = await supabase
                                            .from(JUNGNA_TABLES.banks)
                                            .update({
                                              name: newName,
                                              account_number: newAccountNumber,
                                            })
                                            .eq("id", bank.id);

                                          if (error) {
                                            throw error;
                                          }

                                          alert(
                                            "계좌 정보가 업데이트되었습니다."
                                          );
                                          // 계좌 리스트 새로고침
                                          const fetchBanksAgain = async () => {
                                            if (!userId) return;
                                            const { data, error } =
                                              await supabase
                                                .from(JUNGNA_TABLES.banks)
                                                .select("*")
                                                .eq("user_id", userId);
                                            if (error) {
                                              console.error(
                                                "계좌 가져오기 오류:",
                                                error
                                              );
                                              return;
                                            }
                                            setBanks(data || []);
                                          };
                                          fetchBanksAgain();
                                        } catch (error) {
                                          console.error(
                                            "계좌 업데이트 오류:",
                                            error
                                          );
                                          alert(
                                            "계좌 업데이트 중 오류가 발생했습니다."
                                          );
                                        }
                                      };

                                      updateBank();
                                    }
                                  }
                                }}
                              >
                                수정
                              </ActionButton>
                              <ActionButton
                                status="danger"
                                onClick={() => handleDeleteBank(bank.id)}
                              >
                                삭제
                              </ActionButton>
                            </ButtonGroup>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                  {banks.length > visibleBanks && (
                    <ShowMoreButton onClick={handleShowMoreBanks}>
                      더 보기
                    </ShowMoreButton>
                  )}
                </Section>
              )}
            </>
          )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  padding: 0 10px;
`;

interface TabButtonProps {
  active: boolean;
}

const TabButton = styled.button<TabButtonProps>`
  padding: 15px 30px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) => (props.active ? "#27c360" : "#f8f9fa")};
  color: ${(props) => (props.active ? "white" : "#495057")};
  box-shadow: ${(props) =>
    props.active ? "0 4px 6px rgba(39, 195, 96, 0.2)" : "none"};

  &:hover {
    background-color: ${(props) => (props.active ? "#229a4c" : "#e9ecef")};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  h1 {
    margin: 0;
    color: #2c3e50;
    font-size: 28px;
  }
`;

const HeaderButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const Section = styled.section`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  h2 {
    margin-top: 0;
    margin-bottom: 25px;
    color: #2c3e50;
    font-size: 24px;
    border-bottom: 2px solid #e9ecef;
    padding-bottom: 15px;
  }

  h3 {
    color: #2c3e50;
    font-size: 20px;
    margin-bottom: 20px;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #495057;
    font-size: 15px;
  }

  input,
  textarea,
  select {
    width: 100%;
    padding: 12px;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    font-size: 15px;
    transition: all 0.2s ease;
    background-color: #f8f9fa;

    &:focus {
      outline: none;
      border-color: #27c360;
      background-color: white;
      box-shadow: 0 0 0 3px rgba(39, 195, 96, 0.1);
    }

    &::placeholder {
      color: #adb5bd;
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
  }
`;

const Button = styled.button`
  background-color: #27c360;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background-color: #229a4c;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #adb5bd;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

interface ActionButtonProps {
  status: "success" | "danger" | "primary" | "default";
}

const ActionButton = styled.button<ActionButtonProps>`
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  background-color: ${(props) =>
    props.status === "success"
      ? "#27c360"
      : props.status === "danger"
      ? "#dc3545"
      : props.status === "primary"
      ? "#0d6efd"
      : "#6c757d"};

  &:hover {
    background-color: ${(props) =>
      props.status === "success"
        ? "#229a4c"
        : props.status === "danger"
        ? "#c82333"
        : props.status === "primary"
        ? "#0b5ed7"
        : "#5a6268"};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

interface StatusBadgeProps {
  active: boolean;
}

const StatusBadge = styled.span<StatusBadgeProps>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background-color: ${(props) => (props.active ? "#28a745" : "#6c757d")};
  transition: all 0.2s ease;
`;

const Label = styled.label`
  font-size: 15px;
  color: #495057;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #27c360;
    box-shadow: 0 0 0 3px rgba(39, 195, 96, 0.1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const AddButton = styled(Button)`
  background-color: #27c360;
  padding: 8px 16px;
  font-size: 14px;
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;

  &:hover {
    background-color: #5a6268;
  }
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-top: 20px;

  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #495057;
    font-size: 15px;
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid #e9ecef;

    &:last-child {
      text-align: center;
    }
  }

  td {
    border-bottom: 1px solid #e9ecef;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ShowMoreButton = styled(Button)`
  margin: 20px auto 0;
  background-color: #6c757d;
  width: auto;
  padding: 8px 16px;
  font-size: 14px;

  &:hover {
    background-color: #5a6268;
  }
`;

const ProductItem = styled.tr<{ selected?: boolean }>`
  &:hover {
    background-color: ${(props) => (props.selected ? "#c8e6c9" : "#f8f9fa")};
  }
  background-color: ${(props) => (props.selected ? "#e8f5e9" : "white")};
  transition: background-color 0.2s ease;

  td:first-child {
    border-left: ${(props) => (props.selected ? "4px solid #27c360" : "none")};
  }
`;

const ProductImageCell = styled.td`
  vertical-align: middle;
  padding: 16px;
  width: 80px;
`;

const ProductImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  margin-right: 12px;
`;

const ProductInfo = styled.td`
  vertical-align: middle;
  padding: 16px;
  width: 50%;
`;

const ProductTitle = styled.h3`
  margin: 0 0 4px 0;
  color: #2c3e50;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    color: #27c360;
  }
`;

const ProductDescription = styled.p`
  margin: 0 0 4px 0;
  color: #6c757d;
  font-size: 14px;
`;

const ProductPrice = styled.p`
  margin: 0 0 4px 0;
  color: #27c360;
  font-weight: 600;
  font-size: 15px;
`;

const ProductDate = styled.p`
  margin: 0;
  color: #adb5bd;
  font-size: 13px;
`;

const SelectedInfo = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;

  h3 {
    margin: 0 0 15px 0;
    color: #2c3e50;
    font-size: 18px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 10px;
  align-items: center;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #495057;
`;

const InfoValue = styled.span`
  color: #495057;
`;

const ModalContent = styled.div`
  padding: 20px;
  overflow-y: auto;
  max-height: calc(90vh - 180px);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 20px; /* 좌우 padding 추가 */
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #495057;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #000;
  }
`;

const AddProductModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
`;

interface StepDotProps {
  active: boolean;
  completed: boolean;
}

const StepDot = styled.div<StepDotProps>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.active ? "#27c360" : props.completed ? "#a3e9b9" : "#dee2e6"};
  transition: all 0.2s ease;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px;
  border-top: 1px solid #e9ecef;
  background: white;
  position: sticky;
  bottom: 0;
`;

export default AdminPage;
